import { ILazyLoad, IOptionsConfig } from 'oclazyload';
import angular = require('angular');
import { ModuleLoadingProvider } from './module-loading.provider';
import { ModuleConfig } from '../../angular/routing/models/module-config.interface';
import { FarmnetModuleService } from '../../angular/routing/services/farmnet-module.service';
import { Ng1NgxUrlHandlingStrategyService } from '../../angular/routing/services/ng1-ngx-url-handling-strategy.service';

export class ModuleLoadingService {
    private ocLazyloadConf: IOptionsConfig = {
        reconfig: false,
        rerun: false,
        serie: true,
    };
    private loadedModuleNames: string[] = [];
    private modules: { [moduleName: string]: ModuleConfig } = {};

    constructor(
        private moduleLoadingProvider: ModuleLoadingProvider,
        private farmnetModuleService: FarmnetModuleService,
        private $ocLazyLoad: ILazyLoad,
        private ng1NgXUrlHandlingStrategy: Ng1NgxUrlHandlingStrategyService,
        private $q: angular.IQService,
        private $location: angular.ILocationService,
        private $route: angular.route.IRouteService,
        private routeRedirectService: any,

    ) {
        const modules = farmnetModuleService.getNgModules();
        this.updateModules(modules);

        farmnetModuleService.ngModulesUpdate()
            .subscribe((modules) => {
                this.updateModules(modules);
            });
    }

    private updateModules(modules: ModuleConfig[]) {
        modules.forEach((module) => {
            this.$ocLazyLoad.setModuleConfig(module)
        });

        this.modules = ModuleLoadingService.mapModuleByName(modules);

    }

    isModuleKnown(moduleName: string, mustHaveBeenLoaded: boolean) {
        if (mustHaveBeenLoaded) {
            return this.loadedModuleNames.includes(moduleName);
        } else {
           return this.modules.hasOwnProperty(moduleName);
        }
    }

    resolverLoadLazyByRoute() {
        return this.moduleLoadingProvider.resolverLoadLazyByRoute();
    }

    loadLazyByRoute(previousStagePerRouteslice) {
        const url = this.$location.url();

        if (!this.ng1NgXUrlHandlingStrategy.shouldProcessUrl(url)) {
            let deferred = this.$q.defer();
            let path = this.$location.path();
            let pathArray = path.split('/'); //split path in subroutes
            let currentRouteSlice = pathArray[0] !== '' ? pathArray[0] : pathArray[1];
            // !== "" -> is in this case a route that starts with
            // a slash. Then the second array entry is the right routeslice.
            let previousStage = previousStagePerRouteslice[currentRouteSlice] || 0;
            // keeps track of what has been done before the loadModule was called again
            let targetModule = this.modules[currentRouteSlice];
            // Module that should be loaded if route is same as Modulename...
            let isAlreadyLoaded = this.$ocLazyLoad.isLoaded(currentRouteSlice);
            // ...or that module is already loaded
            let isAllModulesLoaded =
                this.getNotYetLoadedModuleNames().length === 0;
            // all modules have been loaded
            let stage1TryToLoadRouteMod =
                !(targetModule === undefined) &&
                previousStage < 1 &&
                !isAlreadyLoaded &&
                !isAllModulesLoaded;
            let stage2TryToLoadAllOtherMods =
                previousStage < 2 && !isAllModulesLoaded;
            let reloadAndNextStage = prevStage => {
                deferred.reject(); // don't show not-found-page
                previousStagePerRouteslice[currentRouteSlice] = prevStage;

                if (this.routeRedirectService.rejectRouteIfNoRights()) {
                    this.$route.reload(); // try again
                }
            };

            switch (true) {
                case stage1TryToLoadRouteMod:
                    // 1. Load Module - or at least try to load a module that has same name as a first-level route e.g. /reporting
                    this.initiateLoading([targetModule.name])
                        .finally(() => {
                            reloadAndNextStage(1);
                        });
                    break;
                case stage2TryToLoadAllOtherMods:
                    // 2. Now try to load the rest of the modules
                    this.initiateLoadingNotYetLoadedModules()
                        .finally(() => {
                            reloadAndNextStage(2);
                        });
                    break;
                default:
                    // 4. Okay enough, now finally show not-found-page. (after having tried to load every module there is)
                    deferred.resolve();
            }

            return deferred.promise;
        }
    }

    resolverLoadLazy(moduleNameList: string[]) {
        return this.moduleLoadingProvider.resolverLoadLazy(moduleNameList);
    }

    private getNotYetLoadedModuleNames(): string[] {
        return Object.keys(this.modules)
            .filter((moduleName) => {
                return !this.loadedModuleNames.includes(moduleName);
            })
    }

    initiateLoading(toBeLoadedModuleNames: string[]) {
        let angularModuleNamesToLoad = toBeLoadedModuleNames.filter(toBeLoadedModule => {
            return !this.ng1NgXUrlHandlingStrategy.shouldProcessUrl(`/${toBeLoadedModule}`)
        });

        return this.ocLazyLoad(angularModuleNamesToLoad)
            .catch((rejection) => {
                console.warn('error while loading Farmnet module. Trying again with updated moduleInfo', rejection);
                return this.farmnetModuleService.loadAndStoreModuleInfo().toPromise()
                    .then(() => {
                        let modules = this.farmnetModuleService.getNgModules();
                        this.updateModules(modules);
                        return this.ocLazyLoad(angularModuleNamesToLoad);
                    });
            }).catch((err) => {
                console.error(
                    'ocLazyLoad: COULD_NOT_LOAD_MODULE',
                    err,
                    angularModuleNamesToLoad
                );
            });
    }

    private ocLazyLoad(angularModuleNamesToLoad) {
        return this.$ocLazyLoad
            .load(angularModuleNamesToLoad, this.ocLazyloadConf)
            .then(() => {
                angularModuleNamesToLoad.forEach(loadedModuleName => {
                    if (!this.loadedModuleNames.includes(loadedModuleName)) {
                        this.loadedModuleNames.push(loadedModuleName);
                    }
                });
            });
    }

    initiateLoadingNotYetLoadedModules() {
        const moduleNameList = this.getNotYetLoadedModuleNames();

        return this.initiateLoading(moduleNameList);
    }

    private static mapModuleByName(moduleConfigs: ModuleConfig[]) : { [key: string]: ModuleConfig } {
        return moduleConfigs
            .reduce((object, key) => ({ ...object, [key.name]: key }), {});
    }
}
